import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Search, Download, Eye, Calendar, Package, Users, Plus,
  ChevronLeft, ChevronRight, Grid, List, ShoppingCart,
  SlidersHorizontal, LayoutDashboard, Printer, X,
} from "lucide-react";
import OrderDetailsModal from "./OrderDetailsModal";
import OrderStatusBadge  from "./OrderStatusBadge";
import OrdersKanban      from "./OrdersKanban";
import AddOrderModal     from "./Addordermodal";
import { useOrders }     from "../context/OrdersContext";
// At the top of OrdersPage.jsx, add:
import Pagination, { usePagination } from '../shared/Pagination';
import { useLocation } from "react-router-dom";
/* ═══════════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════════ */

const STATUS_TO_COL = {
  "Placed":                   "placed",
  "Processing":               "processing",
  "Printing Started":         "printing_started",
  "Production Completed":     "production_completed",
  "Quality Check":            "quality_check",
  "Packaging":                "packaging",
  "Shipped":                  "shipped",
  "Delivered":                "delivered",
  "Completed":                "completed",
  "Return Requested":         "return_requested",
  "Return Approved":          "return_approved",
  "Return Rejected":          "return_rejected",
  "Returned":                 "returned",
  "Inspection Pending":       "inspection_pending",
  "Inspection Passed":        "inspection_passed",
  "Inspection Failed":        "inspection_failed",
  "Refund Processing":        "refund_processing",
  "Refunded":                 "refunded",
  "Partial Refund":           "partial_refund",
  "Replacement Processing":   "replacement_processing",
  "Cancellation Requested":   "cancellation_requested",
  "Cancelled":                "cancelled",
  "Rejected":                 "rejected",
  "Returned (No Refund)":     "returned_no_refund",
};

const COL_TO_DISPLAY = {
  "placed":                  "Placed",
  "processing":              "Processing",
  "printing_started":        "Printing Started",
  "production_completed":    "Production Completed",
  "quality_check":           "Quality Check",
  "packaging":               "Packaging",
  "shipped":                 "Shipped",
  "delivered":               "Delivered",
  "completed":               "Completed",
  "return_requested":        "Return Requested",
  "return_approved":         "Return Approved",
  "return_rejected":         "Return Rejected",
  "returned":                "Returned",
  "inspection_pending":      "Inspection Pending",
  "inspection_passed":       "Inspection Passed",
  "inspection_failed":       "Inspection Failed",
  "refund_processing":       "Refund Processing",
  "refunded":                "Refunded",
  "partial_refund":          "Partial Refund",
  "replacement_processing":  "Replacement Processing",
  "cancellation_requested":  "Cancellation Requested",
  "cancelled":               "Cancelled",
  "rejected":                "Rejected",
  "returned_no_refund":      "Returned (No Refund)",
};

// Helper: always get a display label regardless of what's stored
const getDisplayStatus = (order) =>
  COL_TO_DISPLAY[order.status] ?? order.rawStatus ?? order.status ?? "Placed";

const VIEW_BUTTONS = [
  { id: "table",  icon: Grid,            label: "Table"  },
  { id: "list",   icon: List,            label: "List"   },
  { id: "kanban", icon: LayoutDashboard, label: "Kanban" },
];

const STATUS_OPTIONS = [
  { value: "all",                    label: "All Status",              group: null },
  { value: "placed",                 label: "Placed",                  group: "Pipeline" },
  { value: "processing",             label: "Processing",              group: "Pipeline" },
  { value: "printing_started",       label: "Printing Started",        group: "Pipeline" },
  { value: "production_completed",   label: "Production Completed",    group: "Pipeline" },
  { value: "quality_check",          label: "Quality Check",           group: "Pipeline" },
  { value: "packaging",              label: "Packaging",               group: "Pipeline" },
  { value: "shipped",                label: "Shipped",                 group: "Pipeline" },
  { value: "delivered",              label: "Delivered",               group: "Pipeline" },
  { value: "completed",              label: "Completed",               group: "Pipeline" },
  { value: "return_requested",       label: "Return Requested",        group: "Returns" },
  { value: "return_approved",        label: "Return Approved",         group: "Returns" },
  { value: "return_rejected",        label: "Return Rejected",         group: "Returns" },
  { value: "returned",               label: "Returned",                group: "Returns" },
  { value: "inspection_pending",     label: "Inspection Pending",      group: "Returns" },
  { value: "partial_refund",         label: "Partial Refund",          group: "Refunds" },
  { value: "replacement_processing", label: "Replacement",             group: "Refunds" },
  { value: "refunded",               label: "Refunded",                group: "Refunds" },
  { value: "cancellation_requested", label: "Cancellation Requested",  group: "Cancelled" },
  { value: "cancelled",              label: "Cancelled",               group: "Cancelled" },
  { value: "rejected",               label: "Rejected",                group: "Cancelled" },
  { value: "returned_no_refund", label: "No Refund (Returned)", group: "Refunds" },
];

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */

const formatDate = (ds) => {
  const d = new Date(ds);
  return [
    String(d.getDate()).padStart(2, "0"),
    String(d.getMonth() + 1).padStart(2, "0"),
    d.getFullYear(),
  ].join("/");
};

export const normalizeOrder = (order) => {
  if (!order) return order;
  const existingName  = order.customer?.name;
  const existingEmail = order.customer?.email;
  if (existingName && existingEmail) return order;
  return {
    ...order,
    customer: {
      ...order.customer,
      name: existingName
        || order.customer_name
        || (order.user ? [order.user.first_name, order.user.last_name].filter(Boolean).join(" ") : "")
        || "",
      email: existingEmail || order.customer_email || order.user?.email || "",
    },
  };
};

const normaliseStatus = (raw = "") => {
  if (!raw) return "placed";
  const s = raw.toLowerCase().trim().replace(/ /g, "_");

  const MAP = {
    "placed":                    "placed",
    "payment_pending":           "placed",
    "payment_success":           "placed",
    "order_created":             "placed",
    "pending_processing":        "placed",
    "processing":                "processing",
    "processing_started":        "processing",
    "printing_started":          "printing_started",
    "production_completed":      "production_completed",
    "quality_check":             "quality_check",
    "packaging":                 "packaging",
    "packed":                    "packaging",
    "shipped":                   "shipped",
    "out_for_delivery":          "shipped",
    "delivered":                 "delivered",
    "completed":                 "completed",
    "return_requested":          "return_requested",
    "return_approved":           "return_approved",
    "return_rejected":           "return_rejected",
    "returned":                  "returned",
    "return_received":           "returned",
    "inspection_pending":        "inspection_pending",
    "inspection_passed":         "inspection_pending",
    "inspection_failed":         "inspection_pending",
    "refund_processing":         "refunded",
    "refund":                    "refunded",
    "refunded":                  "refunded",
    "refund_completed":          "refunded",
    "partial_refund":            "partial_refund",
    "replacement_processing":    "replacement_processing",
    "cancellation_requested":    "cancellation_requested",
    "cancel_requested":          "cancellation_requested",
    "cancelled":                 "cancelled",
    "cancel":                    "cancelled",
    "cancelled-refunded":        "cancelled",
    "cancelled_refunded":        "cancelled",
    "cancelled-notrefunded":     "cancelled",
    "cancelled_notrefunded":     "cancelled",
    "rejected":                  "rejected",
    "returned_no_refund":        "returned_no_refund",
  };

  return MAP[s] || MAP[raw.toLowerCase()] || "placed";
};

const openInvoice = (o) => {
  if (!o) return;
  const norm     = normalizeOrder(o);
  const subtotal = norm.items?.reduce((s, i) => s + i.price * i.quantity, 0) || 0;
  const shipping = norm.shipping ?? 0;
  const tax      = norm.tax      ?? 0;
  const total    = norm.total    ?? subtotal + shipping + tax;

  const fmt = (ds) =>
    ds ? new Date(ds).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" }) : "—";

  const isCustom =
    norm.isCustom === true ||
    norm.productType === "custom" ||
    (norm.items || []).some(
      (i) =>
        i.isCustom === true ||
        i.productType === "custom" ||
        /photo|custom|print|mug|keychain|personali/i.test(i.category || "") ||
        /photo|custom|print|mug|keychain|personali/i.test(i.name || "")
    );

  const rows = (norm.items || []).map((item) => `
    <tr>
      <td style="padding:10px 12px;border-bottom:1px solid #f3f4f6">
        ${item.name}
        ${item.isCustom || item.productType === "custom"
          ? `<span style="font-size:10px;color:#6366f1;font-weight:600;margin-left:6px;padding:1px 6px;background:#eef2ff;border-radius:10px">Custom</span>`
          : ""}
        ${item.variant ? `<br/><span style="font-size:11px;color:#9ca3af">${item.variant}</span>` : ""}
      </td>
      <td style="padding:10px 12px;border-bottom:1px solid #f3f4f6;text-align:center">${item.quantity}</td>
      <td style="padding:10px 12px;border-bottom:1px solid #f3f4f6;text-align:right">₹${item.price?.toFixed(2)}</td>
      <td style="padding:10px 12px;border-bottom:1px solid #f3f4f6;text-align:right;font-weight:600">₹${(item.price * item.quantity).toFixed(2)}</td>
    </tr>`).join("");

  const summaryRows = [["Subtotal", subtotal], ["Tax", tax]].map(([l, v]) => `
    <div style="display:flex;justify-content:space-between;padding:6px 0;font-size:14px;color:#6b7280;border-bottom:1px solid #f3f4f6">
      <span>${l}</span><span style="font-weight:500;color:#374151">₹${Number(v).toFixed(2)}</span>
    </div>`).join("");

  const rawStatus     = norm.rawStatus || norm.status || "";
  const isRefunded    = /refund/i.test(rawStatus);
  const isReplacement = /replacement/i.test(rawStatus);
  const statusLabel   = isReplacement ? "Replacement" : isRefunded ? "Refunded" : "Delivered";
  const statusColor   = isRefunded || isReplacement ? "#16a34a" : "#065f46";
  const statusBg      = isRefunded || isReplacement ? "#dcfce7" : "#d1fae5";

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"/>
<title>Invoice ${norm.orderNumber}</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;color:#111827;background:#fff;padding:40px}@media print{body{padding:20px}}</style>
</head><body><div style="max-width:720px;margin:0 auto">
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:36px;padding-bottom:24px;border-bottom:2px solid #0d9488">
    <div>
      <h1 style="font-size:26px;font-weight:800;color:#0d9488">INVOICE</h1>
      <p style="color:#6b7280;font-size:13px;margin-top:4px">${norm.orderNumber}</p>
      ${isCustom ? `<span style="display:inline-block;margin-top:6px;padding:2px 8px;background:#eef2ff;color:#6366f1;border-radius:10px;font-size:11px;font-weight:700">✦ Custom Print Order</span>` : ""}
    </div>
    <div style="text-align:right">
      <p style="font-size:13px;color:#6b7280">Date</p>
      <p style="font-weight:600;margin-top:2px">${fmt(norm.date)}</p>
      <p style="font-size:13px;color:#6b7280;margin-top:10px">Payment</p>
      <p style="font-weight:600;margin-top:2px">${norm.payment_method || norm.paymentMethod || "COD"}</p>
      <span style="display:inline-block;margin-top:8px;padding:4px 12px;background:${statusBg};color:${statusColor};border-radius:20px;font-size:12px;font-weight:700">${statusLabel}</span>
    </div>
  </div>
  <div style="background:#f9fafb;border-radius:12px;padding:20px;margin-bottom:28px;border:1px solid #e5e7eb">
    <p style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.07em;color:#6b7280;margin-bottom:10px">Bill To</p>
    <p style="font-weight:700;font-size:15px">${norm.customer?.name || "Customer"}</p>
    ${norm.customer?.email  ? `<p style="color:#6b7280;font-size:13px;margin-top:3px">${norm.customer.email}</p>` : ""}
    ${norm.customer?.phone  ? `<p style="color:#6b7280;font-size:13px;margin-top:2px">${norm.customer.phone}</p>` : ""}
    ${norm.shippingAddress  ? `<p style="color:#6b7280;font-size:13px;margin-top:6px">${[norm.shippingAddress.street, norm.shippingAddress.city, norm.shippingAddress.state, norm.shippingAddress.zipCode, norm.shippingAddress.country].filter(Boolean).join(", ")}</p>` : ""}
  </div>
  <table style="width:100%;border-collapse:collapse;margin-bottom:24px">
    <thead><tr style="background:#f3f4f6">
      <th style="padding:10px 12px;text-align:left;font-size:12px;font-weight:700;text-transform:uppercase;color:#6b7280">Item</th>
      <th style="padding:10px 12px;text-align:center;font-size:12px;font-weight:700;text-transform:uppercase;color:#6b7280">Qty</th>
      <th style="padding:10px 12px;text-align:right;font-size:12px;font-weight:700;text-transform:uppercase;color:#6b7280">Price</th>
      <th style="padding:10px 12px;text-align:right;font-size:12px;font-weight:700;text-transform:uppercase;color:#6b7280">Total</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
  <div style="display:flex;justify-content:flex-end;margin-bottom:36px">
    <div style="width:260px">
      ${summaryRows}
      ${shipping ? `<div style="display:flex;justify-content:space-between;padding:6px 0;font-size:14px;color:#6b7280;border-bottom:1px solid #f3f4f6"><span>Shipping</span><span style="font-weight:500;color:#374151">₹${Number(shipping).toFixed(2)}</span></div>` : ""}
      <div style="display:flex;justify-content:space-between;padding:12px 0 4px;font-size:16px;font-weight:800">
        <span>Total</span><span style="color:#0d9488">₹${Number(total).toFixed(2)}</span>
      </div>
    </div>
  </div>
  ${isCustom ? `<div style="background:#eef2ff;border:1px solid #c7d2fe;border-radius:12px;padding:16px;margin-bottom:24px;font-size:12px;color:#4338ca">
    <strong>Custom Print Order Notice:</strong> This order was processed as a custom print product. Returns are only accepted for valid defect or damage claims. See our return policy for details.
  </div>` : ""}
  <div style="border-top:1px solid #e5e7eb;padding-top:20px;text-align:center;color:#9ca3af;font-size:12px">
    <p>Thank you for your order! 🎉</p>
    <p style="margin-top:4px">For any queries, please contact our support team.</p>
  </div>
</div>
<script>window.onload=()=>window.print();</script>
</body></html>`;

  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
};

/* ═══════════════════════════════════════════════════════════
   EXPORT MODAL
═══════════════════════════════════════════════════════════ */

function ExportModal({ open, onClose, filteredOrders }) {
  const [exportFormat, setExportFormat] = useState("csv");
  const [exporting,    setExporting]    = useState(false);

  const downloadCSV = (data) => {
    const cols = ["id", "orderNumber", "status", "productType", "date", "payment_method", "payment_status", "total", "customer_name", "customer_email"];
    const rows = data.map((o) =>
      cols.map((c) => {
        const v = {
          id:             o.id,
          orderNumber:    o.orderNumber,
          status:         o.status,
          productType:    o.isCustom ? "custom" : "standard",
          date:           o.date,
          payment_method: o.payment_method,
          payment_status: o.payment_status,
          total:          o.total,
          customer_name:  o.customer?.name  || o.customer_name,
          customer_email: o.customer?.email || o.customer_email,
        }[c];
        return `"${String(v ?? "").replace(/"/g, '""')}"`;
      }).join(",")
    );
    const blob = new Blob([[cols.join(","), ...rows].join("\r\n")], { type: "text/csv;charset=utf-8;" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: "orders.csv" });
    document.body.appendChild(a); a.click(); a.remove();
  };

  const downloadJSON = (data) => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(blob), download: "orders.json" });
    document.body.appendChild(a); a.click(); a.remove();
  };

  const confirm = async () => {
    if (!filteredOrders.length) return;
    setExporting(true);
    try {
      exportFormat === "csv" ? downloadCSV(filteredOrders) : downloadJSON(filteredOrders);
      onClose();
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:px-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-xl shadow-xl overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold">Export Orders</h3>
          <button onClick={onClose} aria-label="Close" className="p-1.5 rounded-lg text-stone-500 hover:text-stone-700 hover:bg-stone-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 sm:p-6 space-y-4">
          <p className="text-sm text-stone-600">
            Export <strong>{filteredOrders.length}</strong> filtered order{filteredOrders.length !== 1 ? "s" : ""}.
          </p>
          <div className="flex items-center gap-3">
            {["csv", "json"].map((fmt) => (
              <label
                key={fmt}
                className={`flex items-center gap-2 px-4 py-2.5 border rounded-lg cursor-pointer text-sm transition-colors ${
                  exportFormat === fmt ? "bg-teal-50 border-teal-400 text-teal-700" : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                <input
                  type="radio"
                  name="exportFormat"
                  value={fmt}
                  checked={exportFormat === fmt}
                  onChange={() => setExportFormat(fmt)}
                  className="accent-teal-600"
                />
                {fmt.toUpperCase()}
              </label>
            ))}
          </div>
          <p className="text-xs text-stone-400">
            Only orders visible with your current filters will be exported.
          </p>
        </div>
        <div className="px-4 sm:px-6 py-4 border-t bg-stone-50 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={exporting || !filteredOrders.length}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm disabled:opacity-50 hover:bg-emerald-700 transition-colors"
          >
            {exporting ? "Exporting…" : `Export ${exportFormat.toUpperCase()}`}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   FILTER DRAWER (mobile bottom sheet)
═══════════════════════════════════════════════════════════ */

function FilterDrawer({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-end sm:hidden">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full bg-white rounded-t-2xl shadow-xl overflow-hidden">
        {/* drag handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <div className="flex items-center justify-between px-4 pb-3 pt-2 border-b">
          <h3 className="font-semibold text-gray-800">Filters</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-4 space-y-3 pb-8">
          {children}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STAT CARD
═══════════════════════════════════════════════════════════ */

const StatCard = ({ label, value, icon: Icon, subLabel, accent = 'border-l-teal-500', color = 'text-teal-700', bg = 'bg-teal-50' }) => (
  <div className={`bg-white rounded-xl border border-gray-100 border-l-4 ${accent} p-3 sm:p-4 flex items-center gap-3 shadow-sm`}>
    <div className={`w-9 h-9 ${bg} rounded-lg flex items-center justify-center shrink-0`}>
      <Icon className={`w-4 h-4 ${color}`} />
    </div>
    <div className="min-w-0">
      <p className={`text-lg sm:text-xl font-bold ${color} leading-tight`}>{value}</p>
      <p className="text-xs text-gray-400 truncate leading-tight">{label}</p>
      {subLabel && <p className="text-[10px] text-gray-300 mt-0.5">{subLabel}</p>}
    </div>
  </div>
);

/* ═══════════════════════════════════════════════════════════
   MOBILE ORDER CARD
═══════════════════════════════════════════════════════════ */

const MobileOrderCard = ({ order, onView }) => {
  const custom = isCustomOrder(order);
  return (
    <div
      onClick={() => onView(order)}
      className="p-4 border-b last:border-b-0 hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-sm font-semibold text-gray-900 truncate">{order.orderNumber}</span>
          {custom && (
            <span className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold shrink-0">
              <Printer className="w-2.5 h-2.5" />Custom
            </span>
          )}
        </div>
        <span className="text-sm font-bold text-teal-700 shrink-0">₹{Number(order.total)?.toFixed(2)}</span>
      </div>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs text-gray-700 font-medium truncate">
            {order.customer?.name || order.customer_name || "—"}
          </p>
          <p className="text-xs text-gray-400 mt-0.5">{formatDate(order.date)}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <OrderStatusBadge status={getDisplayStatus(order)} />
          <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   CUSTOM ORDER DETECTOR
═══════════════════════════════════════════════════════════ */

// AFTER
const isCustomOrder = (order) =>
  // ✅ Primary: use API-provided customisations field
  order.customisations === "custom" ||
  order.isCustom === true ||
  // Fallback: legacy heuristic for older orders without the field
  (order.customisations == null && (
    order.productType === "custom" ||
    (order.items || []).some(
      (i) =>
        i.isCustom === true ||
        i.productType === "custom" ||
        /photo|custom|print|mug|keychain|personali/i.test(i.category || "") ||
        /photo|custom|print|mug|keychain|personali/i.test(i.name || "")
    )
  ));
/* ═══════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════ */

const OrdersPage = () => {
  const { orders, fetchOrders, fetchOrderById, updateOrder } = useOrders();

  const [addOrderOpen,      setAddOrderOpen]      = useState(false);
  const [filteredOrders,    setFilteredOrders]    = useState([]);
 const location = useLocation();
const [searchTerm, setSearchTerm] = useState(location.state?.prefillSearch || "");
  const [statusFilter,      setStatusFilter]      = useState("all");
  const [dateFilter,        setDateFilter]        = useState("all");
  const [productTypeFilter, setProductTypeFilter] = useState("all");

  const [selectedOrder,     setSelectedOrder]     = useState(null);
  const [isModalOpen,       setIsModalOpen]       = useState(false);
  const [viewFormat,        setViewFormat]        = useState("table");
  const [filterDrawerOpen,  setFilterDrawerOpen]  = useState(false);
  const [exportOpen,        setExportOpen]        = useState(false);
  const [localPatches,      setLocalPatches]      = useState({});

  const timelineCacheRef = useRef({});

  useEffect(() => { fetchOrders(); }, []);

  /* Sync status after modal closes */
  useEffect(() => {
    if (!selectedOrder || isModalOpen) return;
    const fresh = orders.find((o) => o.id === selectedOrder.id);
    if (!fresh) return;
    if (fresh.status !== selectedOrder.status || fresh.rawStatus !== selectedOrder.rawStatus) {
      setSelectedOrder((prev) => prev ? { ...prev, status: fresh.status, rawStatus: fresh.rawStatus } : prev);
    }
  }, [orders, isModalOpen]);

  /* Filter + normalize */
  useEffect(() => {
    let filtered = orders.map((o) => {
      const normalized = normalizeOrder(o);
      const patch      = localPatches[normalized.id];
      if (!patch) return normalized;
      return {
        ...normalized,
        ...patch,
        customer: patch.customer
          ? { ...normalized.customer, ...patch.customer }
          : normalized.customer,
      };
    });

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((o) =>
        (o.orderNumber     || "").toLowerCase().includes(term) ||
        (o.customer?.name  || "").toLowerCase().includes(term) ||
        (o.customer?.email || "").toLowerCase().includes(term)
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (o) => normaliseStatus(o.rawStatus || o.status) === statusFilter
      );
    }

    if (productTypeFilter !== "all") {
      filtered = filtered.filter((o) =>
        productTypeFilter === "custom" ? isCustomOrder(o) : !isCustomOrder(o)
      );
    }

    if (dateFilter !== "all") {
      const now = new Date();
      filtered = filtered.filter((o) => {
        const d = new Date(o.date);
        if (dateFilter === "today") return d.toDateString() === now.toDateString();
        if (dateFilter === "week")  return d >= new Date(now - 7  * 864e5);
        if (dateFilter === "month") return d >= new Date(now - 30 * 864e5);
        return true;
      });
    }

    setFilteredOrders(filtered);
  }, [searchTerm, statusFilter, dateFilter, productTypeFilter, orders, localPatches]);
const {
  page,
  pageSize,
  setPage,
  setPageSize,
  totalPages,
  totalItems,
  paginated: currentOrders,
} = usePagination(filteredOrders, 10);
useEffect(() => { setPage(1); }, [searchTerm, statusFilter, dateFilter, productTypeFilter]);
  // useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter, dateFilter, productTypeFilter]);

  /* Pagination */
  // const totalPages    = Math.ceil(filteredOrders.length / itemsPerPage);
  // const startIndex    = (currentPage - 1) * itemsPerPage;
  // const currentOrders = filteredOrders.slice(startIndex, startIndex + itemsPerPage);

  const stats = {
    total:     orders.length,
    placed:    orders.filter((o) => normaliseStatus(o.rawStatus || o.status) === "placed").length,
    delivered: orders.filter((o) => normaliseStatus(o.rawStatus || o.status) === "delivered").length,
    custom:    orders.filter(isCustomOrder).length,
  };

  /* Open order modal */
  const handleViewOrder = useCallback(async (order) => {
    const current      = filteredOrders.find((o) => o.id === order.id) || order;
    const cached       = timelineCacheRef.current[current.id];
    const withTimeline = cached ? { ...current, timeline: cached } : current;

    setSelectedOrder(withTimeline);
    setIsModalOpen(true);

    const fresh = await fetchOrderById(current.id);
    if (fresh) {
      const freshNorm = normalizeOrder(fresh);
      const bestTimeline =
        (freshNorm.timeline?.length ?? 0) >= (withTimeline.timeline?.length ?? 0)
          ? freshNorm.timeline
          : withTimeline.timeline;

      if (bestTimeline?.length) timelineCacheRef.current[current.id] = bestTimeline;

      setSelectedOrder({
        ...freshNorm,
        timeline:         bestTimeline,
        status_histories: freshNorm.status_histories ?? withTimeline.status_histories,
      });
    }
  }, [fetchOrderById, filteredOrders]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedOrder(null);
  }, []);

  /* Status change from modal */
  const handleStatusChange = useCallback((newStatus, extra = {}) => {
    const colId = STATUS_TO_COL[newStatus] ?? normaliseStatus(newStatus);
    const {
      flagCount, cancelReason, accountBlocked,
      inspectionResult, refundStatus, returnInitiated,
      returnReason, rejectionReason,
      ...shippingExtra
    } = extra;

    setFilteredOrders((prev) =>
      prev.map((o) =>
        o.id === selectedOrder?.id
          ? { ...o, status: colId, rawStatus: newStatus, ...shippingExtra }
          : o
      )
    );

    setSelectedOrder((prev) => {
      if (!prev) return prev;
      if (prev.timeline?.length) timelineCacheRef.current[prev.id] = prev.timeline;
      return {
        ...prev,
        status:    colId,
        rawStatus: newStatus,
        timeline:  prev.timeline,
        ...shippingExtra,
        ...(flagCount      !== undefined && { customerFlags: flagCount }),
        ...(cancelReason   !== undefined && { cancelReason }),
        ...(accountBlocked !== undefined && { accountBlocked }),
        ...(refundStatus   !== undefined && { refundStatus }),
        ...(returnReason   !== undefined && { returnReason }),
      };
    });
  }, [selectedOrder]);

  /* Kanban handlers */
  const handleKanbanStatusChange = useCallback((orderId, newStatus) => {
    updateOrder?.(orderId, { status: newStatus });
    setFilteredOrders((prev) =>
      prev.map((o) => o.id === orderId ? { ...o, status: newStatus } : o)
    );
  }, [updateOrder]);

  const handleKanbanOrderUpdate = useCallback((orderId, patch) => {
    updateOrder?.(orderId, patch);
    setLocalPatches((prev) => {
      const existing = prev[orderId] || {};
      return {
        ...prev,
        [orderId]: {
          ...existing,
          ...patch,
          customer: patch.customer
            ? { ...(existing.customer || {}), ...patch.customer }
            : existing.customer,
        },
      };
    });
  }, [updateOrder]);

  /* Pagination page numbers */
  // const rawPages = [];
  // for (let i = 1; i <= totalPages; i++) {
  //   if (i === 1 || i === totalPages || Math.abs(i - currentPage) <= 1) rawPages.push(i);
  //   else if (Math.abs(i - currentPage) === 2) rawPages.push("…");
  // }
  // const dedupedPages = rawPages.filter((p, i) => !(p === "…" && rawPages[i - 1] === "…"));

  const activeFiltersCount =
    (statusFilter      !== "all" ? 1 : 0) +
    (dateFilter        !== "all" ? 1 : 0) +
    (productTypeFilter !== "all" ? 1 : 0);

  /* Shared sub-components */
  const StatusSelect = ({ className = "", labelPrefix = "" }) => (
    <div className={`flex flex-col gap-1 ${className}`}>
      {labelPrefix && <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{labelPrefix}</label>}
      <select
        value={statusFilter}
        onChange={(e) => setStatusFilter(e.target.value)}
        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
      >
        {STATUS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  const ProductTypeSelect = ({ className = "", labelPrefix = "" }) => (
    <div className={`flex flex-col gap-1 ${className}`}>
      {labelPrefix && <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{labelPrefix}</label>}
      <select
        value={productTypeFilter}
        onChange={(e) => setProductTypeFilter(e.target.value)}
        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
      >
        <option value="all">All Types</option>
        <option value="standard">Standard</option>
        <option value="custom">Custom Print</option>
      </select>
    </div>
  );

  const DateSelect = ({ className = "", labelPrefix = "" }) => (
    <div className={`flex flex-col gap-1 ${className}`}>
      {labelPrefix && <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">{labelPrefix}</label>}
      <select
        value={dateFilter}
        onChange={(e) => setDateFilter(e.target.value)}
        className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white"
      >
        <option value="all">All Time</option>
        <option value="today">Today</option>
        <option value="week">This Week</option>
        <option value="month">This Month</option>
      </select>
    </div>
  );

  const ViewToggle = ({ className = "" }) => (
    <div className={`flex border border-gray-300 rounded-lg overflow-hidden ${className}`}>
      {VIEW_BUTTONS.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          type="button"
          onClick={() => setViewFormat(id)}
          title={label}
          className={`px-2.5 sm:px-3 py-2 flex items-center gap-1.5 text-sm transition-colors ${
            viewFormat === id ? "bg-teal-600 text-white" : "bg-white text-gray-600 hover:bg-gray-50"
          }`}
        >
          <Icon className="w-4 h-4" />
          <span className="hidden lg:inline text-xs">{label}</span>
        </button>
      ))}
    </div>
  );

  /* ══════════════════════════════════════════════════════════
     KANBAN VIEW
  ══════════════════════════════════════════════════════════ */
 if (viewFormat === "kanban") {
  return (
    /* FIX: DashboardHeader above this is h-16 (64px) and sticky. Using
       100vh here made the total page (header + this container) taller
       than the viewport, so the browser page itself scrolled — and
       since this toolbar is a normal flow element (not sticky/fixed),
       it scrolled away with the board instead of staying pinned.
       Subtracting the header's height means header + this container
       together exactly equal the viewport, so the page never scrolls;
       only the board div below (flex-1 min-h-0 overflow-hidden) does. */
    <div className="flex flex-col" style={{ height: "calc(100vh - 64px)" }}>
        {/* Kanban toolbar */}
         <div className="bg-white border-b border-gray-200 px-3 sm:px-5 py-3 flex-shrink-0 shadow-sm">
        
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 shrink-0">
              <div className="w-8 h-8 bg-teal-600 rounded-lg flex items-center justify-center">
                <ShoppingCart className="w-4 h-4 text-white" />
              </div>
              <span className="font-semibold text-teal-800 text-sm hidden sm:block">Orders</span>
            </div>

            {/* Search — grows to fill */}
            <div className="relative flex-1 min-w-0" style={{ minWidth: "120px" }}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
              <input
                type="text"
                placeholder="Search…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>

            {/* Desktop filters inline */}
            <div className="hidden sm:flex items-center gap-2">
              <StatusSelect />
              <ProductTypeSelect />
            </div>

            {/* Mobile filter button */}
            <button
              type="button"
              onClick={() => setFilterDrawerOpen(true)}
              className="sm:hidden relative p-2 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 shrink-0"
            >
              <SlidersHorizontal className="w-4 h-4" />
              {activeFiltersCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-teal-600 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            <div className="ml-auto">
              <ViewToggle />
            </div>
          </div>
        </div>

       <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
          <OrdersKanban
            orders={filteredOrders}
            onStatusChange={handleKanbanStatusChange}
            onOrderUpdate={handleKanbanOrderUpdate}
            onAddOrder={() => {}}
            onDownloadInvoice={openInvoice}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
        </div>

        {/* Mobile filter drawer */}
        <FilterDrawer open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
          <StatusSelect labelPrefix="Status" />
          <ProductTypeSelect labelPrefix="Type" />
          <DateSelect labelPrefix="Date Range" />
          {activeFiltersCount > 0 && (
            <button
              onClick={() => { setStatusFilter("all"); setDateFilter("all"); setProductTypeFilter("all"); setFilterDrawerOpen(false); }}
              className="w-full py-2.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
            >
              Clear all filters
            </button>
          )}
        </FilterDrawer>
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════
     TABLE / LIST VIEW
  ══════════════════════════════════════════════════════════ */
  return (
    <div className="p-3 sm:p-5 lg:p-6 max-w-7xl mx-auto">

      {/* ── Stats Grid ──────────────────────────────────── */}
      {/* 2 cols on mobile, 4 on sm+ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-5">
        <StatCard label="Total Orders"  value={stats.total}     icon={Package}  accent="border-l-teal-500"   color="text-teal-700"   bg="bg-teal-50" />
        <StatCard label="Placed"        value={stats.placed}    icon={Calendar} accent="border-l-blue-400"   color="text-blue-600"   bg="bg-blue-50" />
        <StatCard label="Delivered"     value={stats.delivered} icon={Users}    accent="border-l-green-500"  color="text-green-600"  bg="bg-green-50" />
        <StatCard label="Custom Print"  value={stats.custom}    icon={Printer}  accent="border-l-purple-400" color="text-purple-600" bg="bg-purple-50" subLabel="Strict policy" />
      </div>

      {/* ── Filter Bar ──────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 sm:p-4 mb-4 sm:mb-5 shadow-sm">
        {/* Top row: search + actions */}
        <div className="flex items-center gap-2">
          {/* Search */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4 pointer-events-none" />
            <input
              type="text"
              placeholder="Search orders…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Mobile: filter button */}
          <button
            type="button"
            onClick={() => setFilterDrawerOpen(true)}
            className="sm:hidden relative p-2.5 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 shrink-0 transition-colors"
            aria-label="Open filters"
          >
            <SlidersHorizontal className="w-4 h-4" />
            {activeFiltersCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4.5 h-4.5 min-w-[18px] min-h-[18px] bg-teal-600 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
                {activeFiltersCount}
              </span>
            )}
          </button>

          {/* Desktop: view toggle inline */}
          <ViewToggle className="hidden sm:flex shrink-0" />

          {/* Export */}
          <button
            onClick={() => setExportOpen(true)}
            className="p-2.5 sm:px-3 sm:py-2.5 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 active:scale-95 flex items-center gap-1.5 text-sm shrink-0 transition-all"
            title="Export orders"
          >
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>

          {/* Add Order */}
          <button
            onClick={() => setAddOrderOpen(true)}
            className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white px-3 py-2.5 rounded-xl font-semibold text-sm shrink-0 transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add Order</span>
          </button>
        </div>

        {/* Desktop: filter selects row */}
        <div className="hidden sm:flex items-center gap-2 mt-3 flex-wrap">
          <StatusSelect className="flex-1 min-w-[130px] max-w-[200px]" />
          <ProductTypeSelect className="flex-1 min-w-[120px] max-w-[180px]" />
          <DateSelect className="flex-1 min-w-[120px] max-w-[160px]" />
          <ViewToggle className="sm:hidden" />
          {activeFiltersCount > 0 && (
            <button
              onClick={() => { setStatusFilter("all"); setDateFilter("all"); setProductTypeFilter("all"); }}
              className="px-3 py-2 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap"
            >
              Clear filters ({activeFiltersCount})
            </button>
          )}
        </div>
      </div>

      {/* ── Orders Table / List / Cards ─────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">

        {/* ── TABLE VIEW (hidden on xs, shown on sm+) ── */}
        {viewFormat === "table" && (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead className="bg-teal-700 text-white">
                  <tr>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap">Order ID</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap">Customer</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap hidden md:table-cell">Type</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap hidden lg:table-cell">Order Type</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap hidden lg:table-cell">Date</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap">Status</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap hidden xl:table-cell">Pay Mode</th>
                    <th className="px-3 sm:px-4 py-3 text-left text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap hidden xl:table-cell">Pay Status</th>
                    <th className="px-3 sm:px-4 py-3 text-right text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap">Total</th>
                    <th className="px-3 sm:px-4 py-3 text-center text-xs font-semibold text-white uppercase tracking-wide whitespace-nowrap"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {currentOrders.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="py-16 text-center text-gray-400"> 
                        <Package className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No orders found — try different filters.</p>
                      </td>
                    </tr>
                  ) : currentOrders.map((order) => {
                    const custom = isCustomOrder(order);
                    return (
                      <tr key={order.id} className="hover:bg-gray-50/70 transition-colors">
                        <td className="px-3 sm:px-4 py-3 text-sm font-medium whitespace-nowrap text-gray-900">{order.orderNumber}</td>
                        <td className="px-3 sm:px-4 py-3">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[110px] sm:max-w-[150px] lg:max-w-[180px]">
                            {order.customer?.name || order.customer_name || "—"}
                          </div>
                          <div className="text-xs text-gray-400 truncate max-w-[110px] sm:max-w-[150px] lg:max-w-[180px]">
                            {order.customer?.email || order.customer_email || ""}
                          </div>
                        </td>
                        <td className="hidden md:table-cell px-3 sm:px-4 py-3">
                          {custom ? (
                            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold border border-indigo-200">
                              <Printer className="w-2.5 h-2.5" />Custom
                            </span>
                          ) : (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium">Standard</span>
                          )}
                        </td>
                       <td className="hidden lg:table-cell px-3 sm:px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
  {order.orderTypeLabel ?? "Onsite"}
</td>
<td className="hidden lg:table-cell px-3 sm:px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
  {formatDate(order.date)}
</td>
                        <td className="px-3 sm:px-4 py-3">
                          <OrderStatusBadge status={getDisplayStatus(order)} />
                        </td>
                        <td className="hidden xl:table-cell px-3 sm:px-4 py-3 text-sm text-gray-500">
                          {order.payment_method}
                        </td>
                        <td className="hidden xl:table-cell px-3 sm:px-4 py-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            order.payment_status === "Refunded" ? "bg-red-100 text-red-700"    :
                            order.payment_status === "Paid"     ? "bg-green-100 text-green-700" :
                                                                  "bg-gray-100 text-gray-600"
                          }`}>
                            {order.payment_status}
                          </span>
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-right text-sm font-semibold whitespace-nowrap text-gray-900">
                          ₹{Number(order.total)?.toFixed(2)}
                        </td>
                        <td className="px-3 sm:px-4 py-3 text-center">
                          <button
                            onClick={() => handleViewOrder(order)}
                            title="View order"
                            className="p-1.5 bg-teal-50 text-teal-700 rounded-lg hover:bg-teal-100 transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card view (replaces table on xs) */}
            <div className="sm:hidden">
              {currentOrders.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-14 px-4 text-center">
                  <Package className="w-10 h-10 text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">No orders found — try different filters.</p>
                </div>
              ) : currentOrders.map((order) => (
                <MobileOrderCard key={order.id} order={order} onView={handleViewOrder} />
              ))}
            </div>
          </>
        )}

        {/* ── LIST VIEW ── */}
        {viewFormat === "list" && (
          <div>
            {currentOrders.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-14 text-center px-4">
                <Package className="w-12 h-12 text-gray-300 mb-3" />
                <p className="text-sm text-gray-500">No orders found — try different filters.</p>
              </div>
            ) : currentOrders.map((order) => {
              const custom = isCustomOrder(order);
              return (
                <div
                  key={order.id}
                  onClick={() => handleViewOrder(order)}
                  className="px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 active:bg-gray-100 transition-colors cursor-pointer"
                >
                  {/* Mobile: stacked layout */}
                  <div className="flex items-center justify-between gap-2 sm:hidden">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-gray-900 truncate">{order.orderNumber}</span>
                        {custom && <Printer className="w-3 h-3 text-indigo-500 shrink-0" />}
                      </div>
                      <div className="text-xs text-gray-500 truncate mt-0.5">{order.customer?.name || "—"}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <OrderStatusBadge status={getDisplayStatus(order)} />
                      <span className="text-sm font-semibold text-teal-700">₹{Number(order.total)?.toFixed(2)}</span>
                      <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                    </div>
                  </div>

                  {/* Desktop: 3-col grid */}
                  <div className="hidden sm:grid grid-cols-3 items-center">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5">
                        <span className="text-sm font-semibold text-gray-900 truncate">{order.orderNumber}</span>
                        {custom && <Printer className="w-3 h-3 text-indigo-500 shrink-0" />}
                      </div>
                      <div className="text-xs text-gray-500 truncate mt-0.5">{order.customer?.name || order.customer_name || "—"}</div>
                    </div>
                    <div className="text-xs text-gray-400 whitespace-nowrap text-center">{formatDate(order.date)}</div>
                    <div className="flex items-center justify-end gap-2">
                      <OrderStatusBadge status={getDisplayStatus(order)} />
                      <ChevronRight className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                    </div>
                  </div>
                  
                </div>
              );
            })}
          </div>
          
        )}
      </div>

      {/* ── Pagination ──────────────────────────────────── */}
      {totalPages > 1 && (
  <div className="mt-4 border-t border-gray-100 pt-2">
    <Pagination
      currentPage={page}
      totalPages={totalPages}
      totalItems={totalItems}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={setPageSize}
    />
  </div>
)}

      {/* ── Modals ──────────────────────────────────────── */}
      <OrderDetailsModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        order={selectedOrder}
        onDownloadInvoice={() => openInvoice(selectedOrder)}
        onStatusChange={handleStatusChange}
      />

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        filteredOrders={filteredOrders}
      />

      <AddOrderModal
        open={addOrderOpen}
        onClose={async () => {
          setAddOrderOpen(false);
          await fetchOrders();
        }}
        onSubmit={(newOrder) => { console.log("New order:", newOrder); }}
      />

      {/* Mobile filter bottom-sheet */}
      <FilterDrawer open={filterDrawerOpen} onClose={() => setFilterDrawerOpen(false)}>
        <StatusSelect labelPrefix="Status" />
        <ProductTypeSelect labelPrefix="Type" />
        <DateSelect labelPrefix="Date Range" />
        {activeFiltersCount > 0 && (
          <button
            onClick={() => {
              setStatusFilter("all");
              setDateFilter("all");
              setProductTypeFilter("all");
              setFilterDrawerOpen(false);
            }}
            className="w-full py-2.5 text-sm text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
          >
            Clear all filters
          </button>
        )}
      </FilterDrawer>
    </div>
  );
};

export default OrdersPage;